import { cn } from '@neo/lib/utils'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@neo/primitives/accordion'
import { Button } from '@neo/primitives/button'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import {
  Briefcase,
  Calendar,
  ChevronDown,
  Clock,
  Contact,
  Eye,
  Headphones,
  Inbox,
  ListTodo,
  MessageSquare,
  PhoneOutgoing,
  Settings2,
  UserCog,
  Users,
  UsersRound,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'router-v6'
import { useViewsQuery } from 'v2/pages/conversations/queries'
import { UserRole } from 'v2/types/api/auth'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { sessionHasRole } from 'v2/utils'
import { usePermissions } from '../hooks/use-permissions'
import { useUserPlan } from '../hooks/use-plan'

interface NavItem {
  title: string
  path: string
  icon: React.ReactNode
}

interface SettingsItem {
  title: string
  path: string
  icon: React.ReactNode
  checkPermission: () => boolean
}

const iconProps = { size: 16, strokeWidth: 1.5 }

const useSettingsItems = (): SettingsItem[] => {
  const { hasPermission } = usePermissions()
  const { isFreePlan } = useUserPlan()

  return [
    {
      title: 'Teams',
      path: '/teams',
      icon: <UsersRound {...iconProps} />,
      // Teams was unavailable to free/trial-plan orgs before the SER-5859 split;
      // preserve that gate (mirrors the Users tab's !isFreePlan check).
      checkPermission: () =>
        !isFreePlan && hasPermission(RbacModules.team, RbacPermissionTypes.view),
    },
    {
      title: 'Queues',
      path: '/queues',
      icon: <Users {...iconProps} />,
      checkPermission: () => hasPermission(RbacModules.queue, RbacPermissionTypes.view),
    },
    {
      title: 'Skills',
      path: '/skills',
      icon: <Briefcase {...iconProps} />,
      checkPermission: () => hasPermission(RbacModules.skill, RbacPermissionTypes.view),
    },
    {
      title: 'Availability Status',
      path: '/status',
      icon: <Clock {...iconProps} />,
      checkPermission: () => hasPermission(RbacModules.agentStatus, RbacPermissionTypes.view),
    },
    {
      title: 'Internal Contacts',
      path: '/forwarding-contacts/internal-contacts',
      icon: <Contact {...iconProps} />,
      checkPermission: () =>
        hasPermission(RbacModules.channelCalls, RbacPermissionTypes.view) ||
        hasPermission(RbacModules.channelMessage, RbacPermissionTypes.view),
    },
    {
      title: 'Outbound Configurations',
      path: '/outbound-configuration',
      icon: <PhoneOutgoing {...iconProps} />,
      checkPermission: () =>
        hasPermission(RbacModules.channelCalls, RbacPermissionTypes.view) ||
        hasPermission(RbacModules.channelMessage, RbacPermissionTypes.view) ||
        hasPermission(RbacModules.channelWhatsapp, RbacPermissionTypes.view),
    },
    {
      title: 'Business Hours & Holidays',
      path: '/business-hours-holidays',
      icon: <Calendar {...iconProps} />,
      checkPermission: () =>
        hasPermission(RbacModules.businessHour, RbacPermissionTypes.view) ||
        hasPermission(RbacModules.holiday, RbacPermissionTypes.view),
    },
    {
      title: 'Workload',
      path: '/workload',
      icon: <UserCog {...iconProps} />,
      checkPermission: () =>
        hasPermission(RbacModules.workloadAccessAgent, RbacPermissionTypes.view),
    },
    {
      title: 'Conversation Configurations',
      path: '/workload-access/conversations',
      icon: <MessageSquare {...iconProps} />,
      checkPermission: () =>
        hasPermission(RbacModules.workloadAccessConversations, RbacPermissionTypes.view),
    },
    {
      title: 'Contact View Settings',
      path: '/contact-view-config',
      icon: <Eye {...iconProps} />,
      checkPermission: () => sessionHasRole(UserRole.administrator),
    },
  ]
}

export const HumanSpecialistPopover = () => {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const settingsItems = useSettingsItems()
  const { data: viewsData } = useViewsQuery()

  const isAdmin = sessionHasRole(UserRole.administrator)
  const visibleSettingsItems = settingsItems.filter((item) => item.checkPermission())

  const topItems = useMemo<NavItem[]>(() => {
    const allConversationsView =
      viewsData?.public?.find((view) => view.name === 'All Conversations') ||
      viewsData?.favourite?.find((view) => view.name === 'All Conversations')

    return [
      { title: 'My Inbox', path: '/conversations/active/c', icon: <Inbox {...iconProps} /> },
      { title: 'Tasks', path: '/conversations/tasks/all', icon: <ListTodo {...iconProps} /> },
      {
        title: 'Conversations',
        path: allConversationsView
          ? `/conversations/${allConversationsView.uuid}`
          : '/conversations/live-conversations',
        icon: <MessageSquare {...iconProps} />,
      },
    ]
  }, [viewsData])

  const handleNavigate = (path: string) => {
    navigate(path)
    setOpen(false)
  }

  const isActive = (path: string) => {
    const currentPath = location.pathname.replace(/^\/settings/, '')
    return currentPath === path || currentPath.startsWith(`${path}/`)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="human-specialist-btn" className="gap-2">
          <Headphones className="size-3.5" />
          <span>Human Specialist</span>
          <ChevronDown className="size-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="end" sideOffset={8}>
        <div className="flex flex-col">
          {/* Top level items */}
          {topItems.map((item) => (
            <button
              key={item.path}
              type="button"
              onClick={() => handleNavigate(item.path)}
              className={cn(
                'flex min-h-9 w-full items-center gap-3 rounded-md px-2.5 py-2 text-left text-s-400 text-secondary transition-colors hover:bg-hv1 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isActive(item.path) && 'bg-hv1 text-foreground',
              )}
              aria-current={isActive(item.path) ? 'page' : undefined}
              data-testid={`hs-nav-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {item.icon}
              {item.title}
            </button>
          ))}

          {/* Settings accordion - Admin only */}
          {isAdmin && visibleSettingsItems.length > 0 && (
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="settings" className="border-b-0">
                <AccordionTrigger
                  className="min-h-9 rounded-md px-2.5 py-2 text-s-500 hover:bg-hv1 hover:no-underline"
                  iconClassName="h-3 w-3"
                >
                  <div className="flex items-center gap-3">
                    <Settings2 {...iconProps} />
                    Human Specialist Settings
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-0 pt-0">
                  <div className="flex flex-col pl-4">
                    {visibleSettingsItems.map((item) => (
                      <button
                        key={item.path}
                        type="button"
                        onClick={() => handleNavigate(item.path)}
                        className={cn(
                          'flex min-h-9 w-full items-center gap-3 rounded-md px-2.5 py-2 text-left text-s-400 text-secondary transition-colors hover:bg-hv1 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          isActive(item.path) && 'bg-hv1 text-foreground',
                        )}
                        aria-current={isActive(item.path) ? 'page' : undefined}
                        data-testid={`hs-settings-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <span className="shrink-0" aria-hidden>
                          {item.icon}
                        </span>
                        {item.title}
                      </button>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
